import * as XLSX from 'xlsx'

import { dateFullToday, fechaIsoToYYYY_MM_DD } from '@/utils/utils'
import Margin from '@/interfaces/Margin'

export const getOtherCost = (row: Margin) => row.additionalTotal - row.transferOwnerCost - row.tagCost

/**
 * "Margen Total" = "Margen Vehículo" + "Comisión de Crédito".
 * `row.margin` is the vehicle margin only; the API deliberately leaves the
 * credit commission out of it so it can be shown as its own column.
 */
export const getTotalMargin = (row: Margin) => Number(row.margin ?? 0) + Number(row.creditFee ?? 0)

const normalizePlate = (plate: string) => plate.toUpperCase().replace(/[^A-Z0-9]/g, '')

/**
 * Partial, case-insensitive match on the vehicle plate. Separators are ignored,
 * so "ab-cd 12" matches "ABCD12". An empty query returns every row.
 */
export const filterByPlate = (margins: Margin[], plateQuery: string) => {
  const query = normalizePlate(plateQuery)

  if (!query) return margins

  return margins.filter((row) => normalizePlate(row.plate ?? '').includes(query))
}

const mapDataForExport = (data: Margin[], withAdditionals: boolean) =>
  data.map((row) => ({
    'Nota de Venta': row.number,
    Cliente: `${row.clientFirstname} ${row.clientLastname}`,
    Email: row.clientEmail,
    Teléfono: row.clientPhone,
    Sucursal: row.branch_name || '-',
    Vendedor: `${row.firstname} ${row.lastname}`,
    Fecha: fechaIsoToYYYY_MM_DD(row.date),
    Patente: row.plate,
    Marca: row.brand,
    'Modelo y versión': `${row.model} ${row.version ?? ''}`,
    Color: row.color ?? '',
    Año: Number(row.year),
    Gastos: row.expensesTotal,
    Compra: row.finalCost,
    'Parte de pago': row.tradeInsCount,
    '$ Parte de pago': row.tradeInsPriceTotal,
    'Patentes parte de pago': row.tradeInsPlates,
    Liquidación: row.settlementTotal,
    ...(withAdditionals
      ? {
          Transferencia: row.transferOwnerCost,
          Tag: row.tagCost,
          'Otros adicionales': getOtherCost(row),
        }
      : {}),
    'Valor venta': row.price,
    Descuento: row.discountAmount || 0,
    'Otros ingresos': row.incomesTotal,
    Seguro: row.insuranceCo,
    'Margen Vehículo': row.margin,
    'Comisión de Crédito': row.creditFee,
    Financiera: row.creditInstitution || '-',
    'Margen Total': getTotalMargin(row),
  }))

export const handleExport = (margins: Margin[], withAdditionals = true) => {
  const mappedData = mapDataForExport(margins, withAdditionals)
  const workbook = XLSX.utils.book_new()
  const worksheet = XLSX.utils.json_to_sheet(mappedData)

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Margen')

  const name = `margin-report-${dateFullToday()}.xlsx`

  XLSX.writeFile(workbook, name)
}
